import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, type CleaningAsk } from '@/api/client'

/**
 * The crew asks still waiting on a reply (CAP-710), and what each row needs
 * from them. Kept apart from the components in `crew-asks.tsx` so that file
 * exports components only.
 */

export const WAITING_ASKS_QUERY_KEY = ['cleaning', 'asks', 'waiting'] as const

/** The crew asks still waiting on a reply, shared by every surface that shows them. */
export function useWaitingAsks() {
  return useQuery({
    queryKey: WAITING_ASKS_QUERY_KEY,
    queryFn: () => api.listCleaningAsks({ status: ['waiting'] }),
  })
}

/** What a waiting ask said about one job: who was asked, for when, and when. */
export interface AskedJob {
  sentAt: string
  teamId: string
  scheduledAt: string
}

/** `${property_id}:${job_id}` -> the newest waiting ask that named it. */
export function askedByJob(asks: readonly CleaningAsk[] | undefined): Map<string, AskedJob> {
  const map = new Map<string, AskedJob>()
  for (const ask of asks ?? []) {
    for (const item of ask.items) {
      const key = `${item.property_id}:${item.job_id}`
      const current = map.get(key)
      // The NEWEST ask a job is in is the one its answer belongs to.
      if (!current || current.sentAt < ask.sent_at) {
        map.set(key, { sentAt: ask.sent_at, teamId: ask.team_id, scheduledAt: item.scheduled_at })
      }
    }
  }
  return map
}

/**
 * When this job was put to its crew in a message still waiting on a reply, or
 * null. Every row asks, and React Query serves them all from one request.
 *
 * Only while the job is still what was asked: same crew, same time. A cleaning
 * moved or handed to another crew since was never in that message as it is
 * now, so "Asked" would claim the crew knows something they were not told -
 * the same rule admin-api applies before it accepts the reply (CAP-709).
 */
export function useAskedAt(job: {
  propertyId: string
  jobId: string
  teamId: string | null | undefined
  scheduledAt: string
}): string | null {
  const { data } = useWaitingAsks()
  const byJob = useMemo(() => askedByJob(data?.asks), [data])
  const asked = byJob.get(`${job.propertyId}:${job.jobId}`)
  if (!asked) return null
  if (asked.teamId !== job.teamId) return null
  if (Date.parse(asked.scheduledAt) !== Date.parse(job.scheduledAt)) return null
  return asked.sentAt
}
